import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { opensUpward } from './popupPlacement';
import './Select.css';

// Themed dropdown to replace native <select> — the browser's own dropdown
// ignores the admin theme entirely (system font, OS-native highlight color),
// which is why it looked out of place. Options can be plain strings or
// {value, label} objects.
const normalize = opt => (typeof opt === 'string' ? { value: opt, label: opt } : opt);
// "Other" is always the last choice of a list
const isOther = o => String(o.label).trim().toLowerCase() === 'other' || String(o.value).trim().toLowerCase() === 'other';
const otherLast = list => [...list.filter(o => !isOther(o)), ...list.filter(isOther)];
const SEARCH_FROM = 8; // a list of 8 or more options opens with a search box (searchable={false} switches it off)

// options starting with the typed text come first, then the ones that contain it
const narrow = (list, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    const starts = [], contains = [];
    list.forEach(o => {
        const label = String(o.label).toLowerCase();
        if (label.startsWith(q) || label.split(/[\s/()-]+/).some(word => word.startsWith(q))) starts.push(o);
        else if (label.includes(q)) contains.push(o);
    });
    return [...starts, ...contains];
};

const Select = ({
    value,
    onChange,
    options = [],
    placeholder = 'Select',
    disabled = false,
    className = '',
    allowEmpty = true,
    ariaLabel,
    searchable = 'auto',
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const rootRef = useRef(null);
    const searchRef = useRef(null);
    const [dropUp, setDropUp] = useState(false); // open above the button when there is no room below
    const normalized = options.map(normalize);
    const selected = normalized.find(o => o.value === value);
    const showSearch = searchable === true || (searchable === 'auto' && normalized.length >= SEARCH_FROM);
    const shown = otherLast(narrow(normalized, query));
    const choose = (v) => { onChange(v); setOpen(false); };

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        const onKey = e => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useEffect(() => {
        if (!open) { setQuery(''); setActive(0); return; }
        if (searchRef.current) searchRef.current.focus({ preventScroll: true });
    }, [open]);
    useEffect(() => { setActive(0); }, [query]);
    useEffect(() => {
        if (!open || !rootRef.current) return;
        const el = rootRef.current.querySelector('.ui-select-option.is-active');
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [active, open, query]);

    const onSearchKey = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, Math.max(shown.length - 1, 0))); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); if (shown[active]) choose(shown[active].value); }
    };

    // Closing on disabled toggling (e.g. state cleared while district's menu is open)
    useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

    return (
        <div className={`ui-select ${disabled ? 'is-disabled' : ''} ${className}`} ref={rootRef}>
            <button
                type="button"
                className={`ui-select-trigger ${open ? 'is-open' : ''}`}
                onClick={(event) => {
                    if (disabled) return;
                    if (!open) {
                        // the list's height: its options (34px each, at most 240px) plus the search box when there is one
                        const rows = normalized.length + (allowEmpty ? 1 : 0);
                        setDropUp(opensUpward(event.currentTarget, Math.min(240, 14 + rows * 34 + (showSearch ? 44 : 0))));
                    }
                    setOpen(v => !v);
                }}
                disabled={disabled}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={selected ? 'ui-select-value' : 'ui-select-placeholder'}>
                    {selected ? selected.label : placeholder}
                </span>
                <ChevronDown size={15} className="ui-select-chevron" />
            </button>
            {open && (
                <div className={`ui-select-menu ${dropUp ? 'is-up' : ''}`} role="listbox">
                    {showSearch && (
                        <div className="ui-select-search">
                            <Search size={14} aria-hidden="true" />
                            <input ref={searchRef} type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onSearchKey}
                                placeholder="Type to search" aria-label={(ariaLabel || 'Options') + ' search'} autoComplete="off" spellCheck={false} />
                        </div>
                    )}
                    {allowEmpty && !query.trim() && (
                        <button type="button" className={`ui-select-option ${!value ? 'is-selected' : ''}`}
                            onClick={() => choose('')}>
                            <span className="ui-select-option-placeholder">{placeholder}</span>
                        </button>
                    )}
                    {shown.map((o, i) => (
                        <button
                            type="button"
                            key={o.value}
                            className={`ui-select-option ${o.value === value ? 'is-selected' : ''} ${showSearch && i === active ? 'is-active' : ''}`}
                            onMouseEnter={() => showSearch && setActive(i)}
                            onClick={() => choose(o.value)}
                        >
                            {o.value === value && <Check size={13} className="ui-select-check" />}
                            <span>{o.label}</span>
                        </button>
                    ))}
                    {normalized.length === 0 && <div className="ui-select-empty">No options</div>}
                    {normalized.length > 0 && shown.length === 0 && <div className="ui-select-empty">No match for "{query}"</div>}
                </div>
            )}
        </div>
    );
};

export default Select;
